import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, Search, Check, LoaderCircle } from "lucide-react";
import type { App } from "../api";
import { useVisibleApps } from "../hooks/useVisibleApps";

interface AppSwitcherProps {
  currentApp: App;
}

export default function AppSwitcher({ currentApp }: AppSwitcherProps) {
  const navigate = useNavigate();
  const { apps, loading } = useVisibleApps();

  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(0);

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Sort apps alphabetically and filter by search
  const filteredApps = useMemo(() => {
    const sorted = [...apps].sort((a, b) => a.name.localeCompare(b.name));
    if (!search) return sorted;
    return sorted.filter((app) => app.name.toLowerCase().includes(search.toLowerCase()));
  }, [apps, search]);

  const handleSearchChange = useCallback((value: string) => {
    setSearch(value);
    setHighlightedIndex(0);
  }, []);

  // Focus input when dropdown opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Scroll highlighted item into view
  useEffect(() => {
    if (isOpen && listRef.current) {
      const highlighted = listRef.current.children[highlightedIndex] as HTMLElement;
      if (highlighted) {
        highlighted.scrollIntoView({ block: "nearest" });
      }
    }
  }, [highlightedIndex, isOpen]);

  // Click outside to close
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearch("");
      }
    }

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isOpen]);

  const handleSelect = useCallback(
    (app: App) => {
      if (app.id === currentApp.id) {
        // Just close if selecting current app
        setIsOpen(false);
        setSearch("");
        return;
      }
      navigate(`/apps/${app.id}`);
      setIsOpen(false);
      setSearch("");
    },
    [currentApp.id, navigate]
  );

  const handleKeyDown = (event: React.KeyboardEvent) => {
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        setHighlightedIndex((prev) => (prev < filteredApps.length - 1 ? prev + 1 : prev));
        break;
      case "ArrowUp":
        event.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : 0));
        break;
      case "Enter":
        event.preventDefault();
        if (filteredApps[highlightedIndex]) {
          handleSelect(filteredApps[highlightedIndex]);
        }
        break;
      case "Escape":
        event.preventDefault();
        setIsOpen(false);
        setSearch("");
        break;
    }
  };

  return (
    <div className="relative" ref={containerRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 text-2xl font-bold text-gray-900 dark:text-dark-text hover:text-gray-700 dark:hover:text-dark-text-secondary transition-colors"
      >
        <span>{currentApp.name}</span>
        <ChevronDown className={`w-5 h-5 transition-transform ${isOpen ? "rotate-180" : ""}`} />
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-2 w-80 bg-white dark:bg-dark-bg-secondary rounded-lg shadow-lg border border-gray-200 dark:border-dark-border z-50">
          {/* Search input */}
          <div className="p-3 border-b border-gray-200 dark:border-dark-border">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-dark-text-muted" />
              <input
                ref={inputRef}
                type="text"
                value={search}
                onChange={(e) => handleSearchChange(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Search apps..."
                className="w-full pl-9 pr-3 py-2 text-sm bg-gray-100 dark:bg-dark-bg-tertiary border border-gray-200 dark:border-dark-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-gray-900 dark:text-dark-text placeholder-gray-400 dark:placeholder-dark-text-muted"
              />
            </div>
          </div>

          {/* Apps list */}
          <div ref={listRef} className="max-h-64 overflow-y-auto py-1" onKeyDown={handleKeyDown}>
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <LoaderCircle className="w-5 h-5 text-gray-400 dark:text-dark-text-muted animate-spin" />
              </div>
            ) : filteredApps.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-gray-500 dark:text-dark-text-secondary">
                No apps found
              </div>
            ) : (
              filteredApps.map((app, index) => {
                const isCurrent = app.id === currentApp.id;
                const isHighlighted = index === highlightedIndex;

                return (
                  <button
                    key={app.id}
                    onClick={() => handleSelect(app)}
                    className={`w-full px-4 py-2 text-left text-sm flex items-center justify-between transition-colors ${
                      isHighlighted
                        ? "bg-gray-100 dark:bg-dark-bg-tertiary"
                        : "hover:bg-gray-50 dark:hover:bg-dark-bg-tertiary"
                    } ${
                      isCurrent
                        ? "text-gray-900 dark:text-dark-text font-medium"
                        : "text-gray-700 dark:text-dark-text-secondary"
                    }`}
                  >
                    <span className="truncate">{app.name}</span>
                    {isCurrent && (
                      <Check className="w-4 h-4 text-primary-500 dark:text-primary-400 flex-shrink-0 ml-2" />
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
